import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { usePortalApiRequest } from '../../redux/api/portalApi';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import {
  fieldPayload,
  fieldsForForm,
  moveField,
  normalizeFields,
  normalizeParents,
  listManagedForms,
  belongsToForm,
  FIELD_TYPES,
} from './formManagerUtils';
import AdminFormPreview from './AdminFormPreview';
import './AdminContentEditors.css';
import './AdminFormManager.css';

const errorMessage = (error) =>
  error?.message ||
  error?.data?.message ||
  'The request failed. Please try again.';

export default function AdminFormManager() {
  const url = useSelector(selectApiUrls)?.formFields;
  const parentUrl = useSelector(selectApiUrls)?.formParent;
  const request = usePortalApiRequest();
  const [fields, setFields] = useState([]);
  const [parents, setParents] = useState([]);
  const [parentEditor, setParentEditor] = useState(null);
  const [parentError, setParentError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formSearch, setFormSearch] = useState('');
  const [fieldSearch, setFieldSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [draftOrder, setDraftOrder] = useState(null);
  const [editor, setEditor] = useState(null);
  const [editorError, setEditorError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const realtimeBlocked = loading || busy || Boolean(editor || parentEditor || draftOrder || deleteTarget);
  useGuardedRealtimeList({ apiKey: 'formFields', apply: rows => setFields(normalizeFields(rows)), blocked: realtimeBlocked });
  useGuardedRealtimeList({ apiKey: 'formParent', apply: rows => setParents(normalizeParents(rows)), blocked: realtimeBlocked });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (!url || !parentUrl)
        throw new Error('The form APIs are not configured.');
      const [fieldData, parentData] = await Promise.all([
        request({ url }),
        request({ url: parentUrl }),
      ]);
      setFields(normalizeFields(fieldData));
      setParents(normalizeParents(parentData));
      setLoadFailed(false);
    } catch (err) {
      setLoadFailed(true);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [request, url, parentUrl]);

  useEffect(() => {
    load().catch((err) => setError(errorMessage(err)));
  }, [load]);

  const managedForms = useMemo(
    () => listManagedForms(parents, fields),
    [parents, fields],
  );
  const currentForm =
    managedForms.find((form) => form.key === selected) || managedForms[0];
  const forms = managedForms.map((form) => [
    form.FormName,
    fields.filter((field) => belongsToForm(field, form)).length,
    form.key,
  ]);
  const activeForm = currentForm?.FormName;
  const savedFields = useMemo(
    () =>
      currentForm
        ? fieldsForForm(
            fields
              .filter((field) => belongsToForm(field, currentForm))
              .map((field) => ({ ...field, FormName: currentForm.FormName })),
            activeForm,
          )
        : [],
    [fields, activeForm, currentForm],
  );
  const orderedFields = draftOrder || savedFields;
  const locked = busy || loading || loadFailed;
  const dirty = draftOrder !== null;
  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [dirty]);
  const matchingForms = forms.filter(([name]) =>
    name.toLowerCase().includes(formSearch.toLowerCase().trim()),
  );
  const visibleFields = orderedFields.filter((field) =>
    `${field.FieldName} ${field.FieldDescription}`
      .toLowerCase()
      .includes(fieldSearch.toLowerCase().trim()),
  );

  const openEditor = (field) => {
    setEditorError('');
    setEditor({
      field: field || {
        FieldName: '',
        FieldDescription: '',
        FormName: activeForm,
        FormId: currentForm.Id,
        FieldType: 'text',
        SortOrder:
          Math.max(0, ...savedFields.map((item) => item.SortOrder)) + 1,
      },
      newForm: false,
    });
  };

  const saveField = async (event) => {
    event.preventDefault();
    const data = fieldPayload({
      ...editor.field,
      FormId: currentForm.Id,
      FormName: currentForm.FormName,
      FieldType: editor.field.FieldType || 'text',
    });
    if (!data.FieldName || !data.FormName) {
      setEditorError('Form name and field name are required.');
      return;
    }
    if (
      editor.newForm &&
      forms.some(([name]) => name.toLowerCase() === data.FormName.toLowerCase())
    ) {
      setEditorError('That form already exists. Select it to add fields.');
      return;
    }
    if (
      savedFields.some(
        (field) =>
          field.FormName === data.FormName &&
          field.Id !== data.Id &&
          field.FieldName.toLowerCase() === data.FieldName.toLowerCase(),
      )
    ) {
      setEditorError('A field with that name already exists in this form.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await request({
        url,
        method: data.Id == null ? 'POST' : 'PUT',
        data,
        contract: { requestCase: 'raw' },
      });
      setEditor(null);
      setSelected(String(data.FormId));
      setFormSearch('');
      setFieldSearch('');
      try {
        await load();
        setNotice('Field saved.');
      } catch (err) {
        setError(
          `Field saved, but the list could not be refreshed. ${errorMessage(err)}`,
        );
      }
    } catch (err) {
      setEditorError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openParent = (existing = null) => {
    setParentError('');
    setParentEditor({ existing, FormName: existing?.FormName || '' });
  };

  const saveParent = async (event) => {
    event.preventDefault();
    const name = parentEditor.FormName.trim();
    const existing = parentEditor.existing;
    if (!name) {
      setParentError('Form name is required.');
      return;
    }
    if (
      managedForms.some(
        (form) =>
          form.key !== existing?.key &&
          form.FormName.toLowerCase() === name.toLowerCase(),
      )
    ) {
      setParentError('That form name already exists.');
      return;
    }
    setBusy(true);
    setNotice('');
    setError('');
    setParentError('');
    let parent;
    try {
      if (existing && !existing.orphan) {
        parent = { Id: existing.Id, FormName: name };
        await request({
          url: parentUrl,
          method: 'PUT',
          data: parent,
          contract: { requestCase: 'raw' },
        });
      } else {
        // Read first so retries after an uncertain POST cannot create another parent.
        const latest = normalizeParents(await request({ url: parentUrl }));
        parent = latest.find(
          (item) => item.FormName.toLowerCase() === name.toLowerCase(),
        );
        if (!parent) {
          await request({
            url: parentUrl,
            method: 'POST',
            data: { FormName: name },
            contract: { requestCase: 'raw' },
          });
          const matches = normalizeParents(
            await request({ url: parentUrl }),
          ).filter((item) => item.FormName === name);
          if (matches.length !== 1)
            throw new Error(
              'Unable to verify the new form ID. Refresh before trying again.',
            );
          parent = matches[0];
        }
      }
      if (existing) {
        for (const field of fields.filter((item) =>
          belongsToForm(item, existing),
        )) {
          if (
            field.FormName !== parent.FormName ||
            String(field.FormId) !== String(parent.Id)
          ) {
            await request({
              url,
              method: 'PUT',
              data: fieldPayload({
                ...field,
                FormName: parent.FormName,
                FormId: parent.Id,
              }),
              contract: { requestCase: 'raw' },
            });
          }
        }
      }
      setSelected(String(parent.Id));
      setFormSearch('');
      setFieldSearch('');
      setParentEditor(null);
      await load();
      setNotice(
        existing
          ? 'Form name and fields updated.'
          : 'Form created. You can now add fields.',
      );
    } catch (err) {
      setParentEditor(null);
      setError(
        `The form update could not be completed. Some changes may have saved. Refresh, then use Edit form to retry synchronization. ${errorMessage(err)}`,
      );
      if (parent) setSelected(String(parent.Id));
      try {
        await load();
      } catch (_) {
        /* Refresh remains available. */
      }
    } finally {
      setBusy(false);
    }
  };

  const removeField = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await request({
        url,
        method: 'DELETE',
        data: { Id: deleteTarget.Id },
        contract: { requestCase: 'raw' },
      });
      setDeleteTarget(null);
      try {
        await load();
        setNotice('Field removed.');
      } catch (err) {
        setError(
          `Field removed, but the list could not be refreshed. ${errorMessage(err)}`,
        );
      }
    } catch (err) {
      setError(errorMessage(err));
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  };

  const saveOrder = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    let failure;
    try {
      // The API exposes individual records; reload even after a partial failure.
      for (const [index, field] of draftOrder.entries()) {
        if (field.SortOrder !== index + 1) {
          await request({
            url,
            method: 'PUT',
            data: { ...fieldPayload(field), SortOrder: index + 1 },
            contract: { requestCase: 'raw' },
          });
        }
      }
    } catch (err) {
      failure = err;
    }
    setDraftOrder(null);
    try {
      await load();
      if (failure)
        setError(
          `Some order changes may have saved. The list now shows the saved order; review it and retry. ${errorMessage(failure)}`,
        );
      else setNotice('Field order saved.');
    } catch (err) {
      setError(
        `Unable to refresh the saved order. Refresh before making more changes. ${errorMessage(err)}`,
      );
    } finally {
      setBusy(false);
    }
  };

  const reorder = (from, to) => {
    setDraftOrder(moveField(orderedFields, from, to));
    setNotice('');
  };

  return (
    <section
      className="admin-editor form-manager"
      aria-labelledby="form-manager-title"
    >
      <header className="admin-editor__header">
        <div>
          <p className="admin-editor__eyebrow">Fields &amp; content</p>
          <h2 id="form-manager-title">Form manager</h2>
          <p>
            Organize forms, maintain field details, and control the order in
            which fields appear.
          </p>
        </div>
        <div className="admin-editor__header-actions">
          <Button
            variant="outline-secondary"
            disabled={busy || loading || dirty}
            onClick={() => {
              setError('');
              setNotice('');
              load().catch((err) => setError(errorMessage(err)));
            }}
          >
            Refresh
          </Button>
          <Button disabled={locked || dirty} onClick={() => openParent()}>
            + New form
          </Button>
        </div>
      </header>
      {error && (
        <Alert variant="danger" role="alert">
          {error}
        </Alert>
      )}
      {notice && (
        <Alert variant="success" role="status">
          {notice}
        </Alert>
      )}
      {loading && (
        <div role="status" className="mb-3">
          <Spinner animation="border" size="sm" /> Loading forms…
        </div>
      )}
      <div className="form-manager__layout">
        <aside className="form-manager__sidebar" aria-label="Forms">
          <h3>
            Forms <Badge bg="secondary">{forms.length}</Badge>
          </h3>
          <Form.Control
            aria-label="Search forms"
            placeholder="Search forms…"
            value={formSearch}
            onChange={(event) => setFormSearch(event.target.value)}
          />
          <div className="form-manager__form-list">
            {matchingForms.map(([name, count, key]) => (
              <button
                type="button"
                key={key}
                className={`form-manager__form ${name === activeForm ? 'is-active' : ''}`}
                aria-pressed={name === activeForm}
                disabled={locked || dirty}
                onClick={() => {
                  setSelected(key);
                  setFieldSearch('');
                  setNotice('');
                }}
              >
                <span>{name || '(Unnamed form)'}</span>
                <Badge bg="light" text="dark">
                  {count}
                </Badge>
              </button>
            ))}
            {!matchingForms.length && (
              <p className="text-muted p-3">
                {forms.length
                  ? 'No matching forms.'
                  : 'Your forms will appear here.'}
              </p>
            )}
          </div>
        </aside>
        <div className="form-manager__workspace">
          {activeForm === undefined ? (
            <div className="form-manager__empty">
              <h3>Create your first form</h3>
              <p>
                Give your form a name to get started. Its ID is assigned
                automatically.
              </p>
              <Button disabled={locked} onClick={() => openParent()}>
                + New form
              </Button>
            </div>
          ) : (
            <>
              <div className="form-manager__toolbar">
                <div>
                  <h3>{activeForm || '(Unnamed form)'}</h3>
                  <p className="text-muted mb-0">
                    Form ID: {currentForm.Id ?? 'Not assigned'} ·{' '}
                    {savedFields.length} fields · Ordered by position
                  </p>
                </div>
                <div className="admin-editor__row-actions">
                  <Button
                    variant="outline-secondary"
                    disabled={locked || dirty}
                    onClick={() => openParent(currentForm)}
                  >
                    Edit form
                  </Button>
                  <Button
                    variant="outline-primary"
                    disabled={locked || dirty}
                    onClick={() => setShowPreview(true)}
                  >
                    Preview
                  </Button>
                  <Button
                    disabled={locked || dirty || currentForm.orphan}
                    onClick={() => openEditor()}
                  >
                    + Add field
                  </Button>
                </div>
              </div>
              {currentForm.orphan && (
                <Alert variant="warning">
                  This form has no parent record. Use Edit form to create and
                  link its parent before editing fields.
                </Alert>
              )}
              {!currentForm.orphan &&
                fields.some(
                  (field) =>
                    belongsToForm(field, currentForm) &&
                    (field.FormName !== currentForm.FormName ||
                      field.FormId == null),
                ) && (
                  <Alert variant="warning">
                    Some fields need to be synchronized with this form. Use Edit
                    form and save to synchronize their name and form ID.
                  </Alert>
                )}
              <Form.Control
                className="mb-3"
                aria-label="Search fields"
                placeholder="Search field names or descriptions…"
                value={fieldSearch}
                onChange={(event) => setFieldSearch(event.target.value)}
              />
              {dirty && (
                <Alert variant="warning">
                  <div className="form-manager__toolbar">
                    <span>
                      Unsaved order. Save or discard before changing forms or
                      editing fields.
                    </span>
                    <div className="admin-editor__row-actions">
                      <Button size="sm" disabled={locked} onClick={saveOrder}>
                        Save order
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={locked}
                        onClick={() => setDraftOrder(null)}
                      >
                        Discard
                      </Button>
                    </div>
                  </div>
                </Alert>
              )}
              <p className="text-muted small">
                Use the arrows or choose a position to reorder fields. Clear
                field search to reorder.
              </p>
              <ol className="form-manager__fields">
                {visibleFields.map((field) => {
                  const index = orderedFields.findIndex(
                    (item) => item.Id === field.Id,
                  );
                  return (
                    <li key={field.Id} className="form-manager__field">
                      <span className="form-manager__position">
                        {index + 1}
                      </span>
                      <div className="form-manager__details">
                        <strong>{field.FieldName}</strong>
                        <div className="small text-muted">
                          Field ID: {field.Id} · Form ID:{' '}
                          {field.FormId ?? 'Not assigned'} ·{' '}
                          {FIELD_TYPES.find(
                            ([value]) => value === field.FieldType,
                          )?.[1] ||
                            field.FieldType ||
                            'Type not set'}
                        </div>
                        <p>
                          {field.FieldDescription || 'No description provided.'}
                        </p>
                      </div>
                      <div className="form-manager__controls">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          aria-label={`Move ${field.FieldName} up`}
                          disabled={
                            locked || Boolean(fieldSearch.trim()) || index === 0
                          }
                          onClick={() => reorder(index, index - 1)}
                        >
                          ↑
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          aria-label={`Move ${field.FieldName} down`}
                          disabled={
                            locked ||
                            Boolean(fieldSearch.trim()) ||
                            index === orderedFields.length - 1
                          }
                          onClick={() => reorder(index, index + 1)}
                        >
                          ↓
                        </Button>
                        <Form.Select
                          size="sm"
                          aria-label={`Position for ${field.FieldName}`}
                          value={index}
                          disabled={locked || Boolean(fieldSearch.trim())}
                          onChange={(event) =>
                            reorder(index, Number(event.target.value))
                          }
                        >
                          {orderedFields.map((_, position) => (
                            <option key={position} value={position}>
                              {position + 1}
                            </option>
                          ))}
                        </Form.Select>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={locked || dirty || currentForm.orphan}
                          onClick={() => openEditor(field)}
                        >
                          Edit
                          <span className="visually-hidden">
                            {' '}
                            {field.FieldName}
                          </span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={locked || dirty}
                          onClick={() => {
                            setError('');
                            setDeleteTarget(field);
                          }}
                        >
                          Remove
                          <span className="visually-hidden">
                            {' '}
                            {field.FieldName}
                          </span>
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ol>
              {!visibleFields.length && (
                <p className="form-manager__empty">
                  No fields match your search.
                </p>
              )}
            </>
          )}
        </div>
      </div>
      <Modal
        show={Boolean(editor)}
        onHide={() => !busy && setEditor(null)}
        backdrop="static"
      >
        <Form onSubmit={saveField}>
          <Modal.Header closeButton={!busy}>
            <Modal.Title>
              {editor?.newForm
                ? 'Create form'
                : editor?.field.Id != null
                  ? 'Edit field'
                  : 'Add field'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editorError && (
              <Alert variant="danger" role="alert">
                {editorError}
              </Alert>
            )}
            {editor && (
              <fieldset disabled={busy}>
                <div className="d-flex gap-3 mb-3">
                  <Form.Group controlId="form-manager-field-id">
                    <Form.Label>Field ID</Form.Label>
                    <Form.Control
                      readOnly
                      value={editor.field.Id ?? 'Assigned when saved'}
                    />
                  </Form.Group>
                  <Form.Group controlId="form-manager-form-id">
                    <Form.Label>Form ID</Form.Label>
                    <Form.Control
                      readOnly
                      value={currentForm?.Id ?? 'Not assigned'}
                    />
                  </Form.Group>
                </div>
                <Form.Group controlId="form-manager-name" className="mb-3">
                  <Form.Label>Form name</Form.Label>
                  <Form.Control
                    required
                    value={editor.field.FormName}
                    readOnly={!editor.newForm}
                    onChange={(event) =>
                      setEditor({
                        ...editor,
                        field: {
                          ...editor.field,
                          FormName: event.target.value,
                          SortOrder: 1,
                        },
                      })
                    }
                  />
                  {editor.newForm && (
                    <Form.Text>
                      Create a form by adding its first field. Fields sharing
                      this name belong to the same form.
                    </Form.Text>
                  )}
                </Form.Group>
                <Form.Group
                  controlId="form-manager-field-name"
                  className="mb-3"
                >
                  <Form.Label>Field name</Form.Label>
                  <Form.Control
                    autoFocus
                    required
                    value={editor.field.FieldName}
                    onChange={(event) =>
                      setEditor({
                        ...editor,
                        field: {
                          ...editor.field,
                          FieldName: event.target.value,
                        },
                      })
                    }
                    placeholder="e.g. ProjectManager"
                  />
                </Form.Group>
                <Form.Group controlId="form-manager-description">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={editor.field.FieldDescription}
                    onChange={(event) =>
                      setEditor({
                        ...editor,
                        field: {
                          ...editor.field,
                          FieldDescription: event.target.value,
                        },
                      })
                    }
                  />
                </Form.Group>
                <Form.Group
                  controlId="form-manager-field-type"
                  className="mt-3"
                >
                  <Form.Label>Field type</Form.Label>
                  <Form.Select
                    value={editor.field.FieldType || 'text'}
                    onChange={(event) =>
                      setEditor({
                        ...editor,
                        field: {
                          ...editor.field,
                          FieldType: event.target.value,
                        },
                      })
                    }
                  >
                    {editor.field.FieldType &&
                      !FIELD_TYPES.some(
                        ([type]) => type === editor.field.FieldType,
                      ) && (
                        <option value={editor.field.FieldType}>
                          {editor.field.FieldType}
                        </option>
                      )}
                    {FIELD_TYPES.map(([type, label]) => (
                      <option key={type} value={type}>
                        {label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </fieldset>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              disabled={busy}
              onClick={() => setEditor(null)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy
                ? 'Saving…'
                : editor?.newForm
                  ? 'Create form'
                  : 'Save field'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal
        show={Boolean(deleteTarget)}
        onHide={() => !busy && setDeleteTarget(null)}
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Remove field?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Remove <strong>{deleteTarget?.FieldName}</strong> from{' '}
          <strong>{deleteTarget?.FormName}</strong>?
          {savedFields.length === 1 && (
            <p className="mt-3 mb-0">
              This is the last field. The form will remain available so you can
              add fields later.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            disabled={busy}
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </Button>
          <Button variant="danger" disabled={busy} onClick={removeField}>
            {busy ? 'Removing…' : 'Remove field'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={Boolean(parentEditor)}
        onHide={() => !busy && setParentEditor(null)}
        backdrop="static"
      >
        <Form onSubmit={saveParent}>
          <Modal.Header closeButton={!busy}>
            <Modal.Title>
              {parentEditor?.existing ? 'Edit form' : 'Create form'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {parentError && <Alert variant="danger">{parentError}</Alert>}
            <Form.Group controlId="parent-form-id" className="mb-3">
              <Form.Label>Form ID</Form.Label>
              <Form.Control
                readOnly
                value={
                  parentEditor?.existing && !parentEditor.existing.orphan
                    ? parentEditor.existing.Id
                    : 'Assigned when created'
                }
              />
            </Form.Group>
            <Form.Group controlId="parent-form-name">
              <Form.Label>Form name</Form.Label>
              <Form.Control
                autoFocus
                required
                disabled={busy}
                value={parentEditor?.FormName || ''}
                onChange={(event) =>
                  setParentEditor({
                    ...parentEditor,
                    FormName: event.target.value,
                  })
                }
              />
              <Form.Text>
                Changing this title also updates every field linked to the form.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              disabled={busy}
              onClick={() => setParentEditor(null)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy
                ? 'Saving…'
                : parentEditor?.existing
                  ? 'Save form'
                  : 'Create form'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      {showPreview && currentForm && (
        <AdminFormPreview
          parent={currentForm}
          fields={savedFields}
          onHide={() => setShowPreview(false)}
        />
      )}
    </section>
  );
}
